import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Alert, Card, Form, Row, Col, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { bookingSuggestionTitle, proposalFromTemplateResponse, proposalsFromResponse } from '../../../services/BookingProposalService';
import {
  getBookkeepingTemplate,
  listBookkeepingTemplates,
  recordTemplateUsage,
  suggestVerification as suggestFromTemplate,
} from '../../../services/BookkeepingTemplateService';
import useBookingProposalReview from '../../../hooks/useBookingProposalReview';
import { resolveRoleAccounts, roleSettingsPath } from '../../../services/api/chartRolesApi';
import {
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  Lightbulb,
  Edit3,
  Plus,
  Trash2
} from 'lucide-react';

const round2 = (n) => Math.round(n * 100) / 100;

const emptyRow = () => ({ account_number: '', account_name: '', debit: '', credit: '' });

/**
 * Two-stage flow for creating a verification from a known transaction.
 * Stage 1 chooses the source of the posting: automatic proposal, template,
 * or fully manual. Choosing a ready proposal advances immediately.
 * Stage 2 combines review and optional row editing with the final booking
 * action, so there is no separate confirmation screen repeating the data.
 *
 * Optional `initialRows` prop ([{ account_number, account_name, debit,
 * credit }, ...], min 2 rows): prefills the row editor and skips straight
 * to step 2 — used by the Matchning page's "Bokför överföring" action to
 * seed the two transfer legs. When omitted, the wizard behaves as before.
 */
const CreateVerificationFromTransactionModal = ({ show, onHide, transaction, onSuccess, initialRows = null, bankLeg = null }) => {
  const { companyId } = useParams();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);

  // Data state
  const [suggestions, setSuggestions] = useState([]);
  // Learning-loop join key (§4.5): stamped by the backend when automatic
  // or template suggestions are shown, then echoed on the booking POST.
  const [suggestionGroupId, setSuggestionGroupId] = useState(null);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  // Series is V for all verifikat created here (owner decision
  // 2026-07-26: the company books everything in one V series — not a
  // user choice for now).
  const VERIFICATION_SERIES = 'V';
  const [accounts, setAccounts] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [fiscalYearsError, setFiscalYearsError] = useState(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [discoveryMode, setDiscoveryMode] = useState('suggestions');
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateInputs, setTemplateInputs] = useState({});
  const [templateError, setTemplateError] = useState(null);
  const [templateEvaluating, setTemplateEvaluating] = useState(false);
  const [showAdvancedRows, setShowAdvancedRows] = useState(false);

  // Detect transaction type (bank vs skatteverket)
  const transactionType = transaction?.statement_id ? 'skatteverket' : 'bank';
  const statementId = transaction?.statement_id || transaction?.id;

  // Default leg for the money account: the bank account's MAPPED kontoplan
  // account when the caller supplies one (bank_account_mappings — owner
  // feedback 2026-07-26), else the tenant's chart role — primary_bank for
  // bank lines, tax_account for Skatteverket lines (mirrors the backend's
  // SkatteverketVerificationService role routing). Never a hardcoded number:
  // an unresolved role is an actionable needs-mapping state.
  const [roleLegs, setRoleLegs] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    let active = true;
    resolveRoleAccounts(companyId, ['primary_bank', 'tax_account']).then((legs) => {
      if (active) setRoleLegs(legs);
    });
    return () => { active = false; };
  }, [companyId]);
  const roleLeg = (role) => (roleLegs && roleLegs[role]
    ? { number: roleLegs[role].accountNumber, name: roleLegs[role].accountName }
    : null);
  const defaultLegAccount = transactionType === 'skatteverket'
    ? roleLeg('tax_account')
    : (bankLeg
      ? { number: bankLeg.account_number, name: bankLeg.account_name }
      : roleLeg('primary_bank'));
  const neededRole = transactionType === 'skatteverket' ? 'tax_account' : 'primary_bank';
  const roleLegMissing = Boolean(roleLegs) && !defaultLegAccount;

  const proposalReview = useBookingProposalReview({ transaction, defaultLegAccount });
  const {
    proposal: selectedSuggestion,
    rows,
    setRows,
    selectProposal,
    startManual,
    reset: resetProposal,
    balance: { totalDebit, totalCredit, isBalanced },
  } = proposalReview;

  const fetchFiscalYears = useCallback(async () => {
    setFiscalYearsError(null);
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/accounting/fiscalyears`),
        { withCredentials: true }
      );

      const years = response.data.fiscalYears || [];
      setFiscalYears(years);

      // Auto-select current fiscal year
      const current = years.find(fy => fy.status === 'active' || fy.is_current);
      if (current) {
        setSelectedFiscalYear(current.fiscal_year_id);
      } else if (years.length > 0) {
        setSelectedFiscalYear(years[0].fiscal_year_id);
      }
    } catch (err) {
      console.error('Error fetching fiscal years:', err);
      setFiscalYearsError('Kunde inte hämta räkenskapsår — försök igen');
    }
  }, [companyId]);

  const fetchAccounts = useCallback(async () => {
    try {
      const result = await axios.get(
        getApiUrl(`${companyId}/accounting/accounts?scope=company`),
        { withCredentials: true }
      );
      const options = (result.data.accounts || []).map(account => ({
        value: String(account.account_number),
        label: `${account.account_number} - ${account.account_name}`,
        account_number: String(account.account_number),
        account_name: account.account_name
      }));
      setAccounts(options);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, [companyId]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplateError(null);
    try {
      setTemplates(await listBookkeepingTemplates(companyId));
    } catch (err) {
      console.error('Error fetching bookkeeping templates:', err);
      setTemplates([]);
      setTemplateError('Kunde inte hämta bokföringsmallar');
    } finally {
      setTemplatesLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (show && transaction) {
      resetWizard();
      fetchFiscalYears();
      fetchAccounts();
      fetchTemplates();

      if (initialRows && initialRows.length >= 2) {
        // Caller-provided prefill (e.g. transfer legs) — skip the
        // suggestion step and land directly in the row editor.
        startManual(initialRows);
        setShowAdvancedRows(true);
        setCurrentStep(2);
      } else {
        fetchSuggestions();
      }

      // Prefill description/notes up front — not inside the suggestion
      // fetch — so a failed suggestion fetch never leaves them empty.
      // REFERENCE first (owner feedback 2026-07-26): on Swedish bank
      // exports `description` is just the rail type ("Kortköp/uttag");
      // the merchant/message lives in `reference` ("OPENAI *CHATGPT…") —
      // that is what a verifikat should be named after. The rail label
      // remains as fallback and in the notes.
      const sourceLabel = transactionType === 'skatteverket' ? 'skattekontotransaktion' : 'banktransaktion';
      setDescription(transaction.reference || transaction.description ||
        (transactionType === 'skatteverket' ? 'Skattekontotransaktion' : 'Banktransaktion'));
      setNotes(`Skapad från ${sourceLabel}: ${[transaction.description, transaction.reference].filter(Boolean).join(' · ')}`.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, transaction]);

  const resetWizard = () => {
    setCurrentStep(1);
    setSuggestions([]);
    setSuggestionGroupId(null);
    resetProposal();
    setDiscoveryMode('suggestions');
    setTemplateSearch('');
    setSelectedTemplate(null);
    setTemplateInputs({});
    setTemplateError(null);
    setShowAdvancedRows(false);
    setDescription('');
    setNotes('');
    setError(null);
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    // Clear before flight — a failed fetch must never leave a previous
    // transaction's suggestions selectable against this line.
    setSuggestions([]);
    setSuggestionGroupId(null);
    resetProposal();

    try {
      const basePath = transactionType === 'skatteverket'
        ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification-suggestions`
        : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification-suggestions`;

      const apiUrl = getApiUrl(basePath);

      const response = await axios.get(apiUrl, { withCredentials: true });

      if (response.data.success) {
        const fetched = proposalsFromResponse(response.data, {
          transaction,
          defaultLegAccount,
          source: transactionType === 'skatteverket' ? 'tax_authority' : 'bank',
          accountNameFor,
        });
        setSuggestions(fetched);
        setSuggestionGroupId(response.data.suggestion_group_id || null);

      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Kunde inte hämta kontoförslag');
    } finally {
      setLoading(false);
    }
  };

  const accountNameFor = (accountNumber) => {
    const match = accounts.find(option => option.value === String(accountNumber));
    return match ? match.account_name : '';
  };

  // Build the two prefill rows (money leg + counter leg) for the given
  // counter account. Sign convention: positive amount = money in.
  const buildPrefillRows = (counterAccountNumber, counterAccountName) => {
    const amount = round2(Math.abs(parseFloat(transaction.amount)));
    const incoming = parseFloat(transaction.amount) > 0;

    // The money-leg role may be unresolved (R1 needs-mapping) or still
    // loading — the manual flow must still open, with an empty account the
    // user fills in (the needs-mapping Alert above explains why).
    const leg = defaultLegAccount || { number: '', name: '' };
    const legRow = {
      account_number: leg.number,
      account_name: accountNameFor(leg.number) || leg.name,
      debit: incoming ? amount : '',
      credit: incoming ? '' : amount
    };
    const counterRow = {
      account_number: counterAccountNumber ? String(counterAccountNumber) : '',
      account_name: counterAccountNumber
        ? (accountNameFor(counterAccountNumber) || counterAccountName || '')
        : '',
      debit: incoming ? '' : amount,
      credit: incoming ? amount : ''
    };

    return [legRow, counterRow];
  };

  const handleSuggestionSelect = (suggestion) => {
    setSelectedTemplate(null);
    setTemplateInputs({});
    selectProposal(suggestion);
    setSuggestionGroupId(suggestion.suggestion_group_id || suggestionGroupId);
    setShowAdvancedRows(false);
    setCurrentStep(2);
  };

  const handleSkipSuggestions = () => {
    setSelectedTemplate(null);
    setTemplateInputs({});
    startManual(buildPrefillRows(null, null));
    setShowAdvancedRows(true);
    setCurrentStep(2);
  };

  const handleTemplateSelect = async (template) => {
    setTemplateError(null);
    setTemplateEvaluating(true);
    try {
      const fullTemplate = template.body
        ? template
        : await getBookkeepingTemplate(companyId, template.template_id);
      const initialInputs = {};
      for (const input of fullTemplate.body?.inputs || []) {
        if (input.default !== undefined) {
          initialInputs[input.name] = input.default;
        } else if (input.example !== undefined) {
          initialInputs[input.name] = input.example;
        } else if (['currency', 'currency_gross', 'currency_net'].includes(input.type)) {
          initialInputs[input.name] = Math.abs(Number(transaction.amount) || 0);
        }
      }
      setSelectedTemplate(fullTemplate);
      setTemplateInputs(initialInputs);
    } catch (err) {
      console.error('Error loading bookkeeping template:', err);
      setTemplateError('Kunde inte ladda mallen');
    } finally {
      setTemplateEvaluating(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    setTemplateEvaluating(true);
    setTemplateError(null);
    try {
      const result = await suggestFromTemplate(
        companyId,
        selectedTemplate.template_id,
        templateInputs,
        {
          statement_id: statementId,
          statement_source: transactionType === 'skatteverket' ? 'tax_authority' : 'bank',
        }
      );
      const proposal = proposalFromTemplateResponse(result, selectedTemplate, {
        transaction,
        defaultLegAccount,
        accountNameFor,
      });
      if (!result?.ok || !proposal) {
        const message = (result?.errors || []).map((item) => item.error || item.message || String(item)).join(', ');
        setTemplateError(message || 'Mallen kunde inte skapa ett balanserat förslag');
        return;
      }
      selectProposal(proposal);
      setSuggestionGroupId(proposal.suggestion_group_id || null);
      setShowAdvancedRows(false);
      setCurrentStep(2);
    } catch (err) {
      console.error('Error evaluating bookkeeping template:', err);
      setTemplateError(err.response?.data?.message || 'Kunde inte använda mallen');
    } finally {
      setTemplateEvaluating(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError(null);
  };

  const validateRows = () => {
    if (rows.length < 2) return false;

    for (const row of rows) {
      if (!row.account_number) return false;
      if (!(parseFloat(row.debit) || 0) && !(parseFloat(row.credit) || 0)) return false;
    }

    return isBalanced;
  };

  const handleCreateVerification = async () => {
    if (!selectedFiscalYear) {
      setError('Välj räkenskapsår');
      return;
    }
    if (!validateRows()) {
      setError('Kontrollera att alla konton är ifyllda och att debet = kredit');
      setShowAdvancedRows(true);
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const basePath = transactionType === 'skatteverket'
        ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification`
        : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification`;

      const apiUrl = getApiUrl(basePath);

      // New backend contract (audit F1/B1): full editable rows, resolved
      // by account_number server-side. Legacy accountMappings shape retired.
      const requestBody = {
        rows: rows.map(row => ({
          account_number: String(row.account_number),
          debit: round2(parseFloat(row.debit) || 0),
          credit: round2(parseFloat(row.credit) || 0)
        })),
        fiscal_year_id: selectedFiscalYear,
        series: VERIFICATION_SERIES,
        description,
        notes,
        // Echo the learning-loop join key so the backend can record the
        // accept/edit outcome against the suggestions it showed (§4.5).
        ...(suggestionGroupId ? { suggestion_group_id: suggestionGroupId } : {}),
        // Only flag used_suggestion when a canonical automatic/template
        // proposal actually seeded the editable rows. Manual and initialRows
        // paths clear the proposal link.
        ...(selectedSuggestion ? { used_suggestion: true, proposal: selectedSuggestion } : {})
      };

      const response = await axios.post(apiUrl, requestBody, { withCredentials: true });

      if (response.data.success) {
        if (selectedTemplate?.template_id) {
          recordTemplateUsage(companyId, selectedTemplate.template_id);
        }
        if (onSuccess) {
          onSuccess(response.data);
        }
        onHide();
      }
    } catch (err) {
      console.error('Error creating verification:', err);
      setError(err.response?.data?.message || 'Kunde inte skapa verifikat');
    } finally {
      setCreating(false);
    }
  };

  const updateRow = (index, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAccountSelect = (index, option) => {
    setRows(prev => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        account_number: option ? option.account_number : '',
        account_name: option ? option.account_name : ''
      };
      return next;
    });
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, emptyRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows(prev => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const getConfidenceBadgeColor = (confidence) => {
    if (!Number.isFinite(confidence)) return 'secondary';
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.7) return 'info';
    return 'warning';
  };

  const getConfidenceLabel = (suggestion) => {
    const tier = suggestion.confidence_tier;
    if (tier === 'high') return 'Hög träffsäkerhet';
    if (tier === 'medium') return 'Medel träffsäkerhet';
    if (tier === 'low') return 'Behöver granskas';
    return 'Granska förslaget';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const evidenceLabel = (item) => (
    typeof item === 'string'
      ? item
      : (item?.label || item?.reason || item?.description || null)
  );

  const warningLabel = (item) => (
    typeof item === 'string'
      ? item
      : (item?.message || item?.error || item?.label || null)
  );

  if (!transaction) return null;

  const isIncoming = parseFloat(transaction.amount) > 0;
  const visibleTemplates = templates.filter((template) => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return true;
    return `${template.template_name || ''} ${template.description || ''}`.toLowerCase().includes(query);
  });
  // With caller-provided rows the wizard starts at step 2 — "Tillbaka"
  // from there is a cancel, not a trip to the empty suggestion step.
  const startStep = initialRows && initialRows.length >= 2 ? 2 : 1;
  const rowsAreComplete = rows.length >= 2 && rows.every((row) => (
    row.account_number && ((parseFloat(row.debit) || 0) > 0 || (parseFloat(row.credit) || 0) > 0)
  ));

  const rowSelectValue = (row) => (
    row.account_number
      ? {
        value: String(row.account_number),
        label: row.account_name
          ? `${row.account_number} - ${row.account_name}`
          : String(row.account_number)
      }
      : null
  );

  const renderRowsTable = () => (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th style={{ fontSize: '0.75rem' }}>Konto</th>
            <th style={{ fontSize: '0.75rem' }} className="text-end">Debet</th>
            <th style={{ fontSize: '0.75rem' }} className="text-end">Kredit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.account_number}-${index}`}>
              <td style={{ fontSize: '0.8125rem' }}>
                {row.account_number || 'Konto saknas'}
                {row.account_name ? ` – ${row.account_name}` : ''}
              </td>
              <td style={{ fontSize: '0.8125rem' }} className="text-end">
                {(parseFloat(row.debit) || 0) > 0 ? formatCurrency(parseFloat(row.debit)) : '–'}
              </td>
              <td style={{ fontSize: '0.8125rem' }} className="text-end">
                {(parseFloat(row.credit) || 0) > 0 ? formatCurrency(parseFloat(row.credit)) : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#eff6ff',
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} />
          </div>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Bokför transaktion
            </span>
            <div style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
              Steg {currentStep} av 2
            </div>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Stäng"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Progress Bar */}
        <ProgressBar className="mb-4" style={{ height: '6px' }}>
          <ProgressBar variant="primary" now={(currentStep / 2) * 100} />
        </ProgressBar>

        {/* Transaction Info Summary */}
        <Card className="mb-4" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  Transaktion
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                  {transaction.description}
                </div>
              </div>
              <div className="text-end">
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  Belopp
                </div>
                <div
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: isIncoming ? '#16a34a' : '#dc2626'
                  }}
                >
                  {isIncoming ? <TrendingUp size={16} className="me-1" /> : <TrendingDown size={16} className="me-1" />}
                  {formatCurrency(transaction.amount)}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Fiscal year fetch failure — blocks submit until resolved */}
        {fiscalYearsError && (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between">
            <span>
              <AlertCircle size={16} className="me-2" />
              {fiscalYearsError}
            </span>
            <Button size="sm" variant="outline-danger" onClick={fetchFiscalYears}>
              Försök igen
            </Button>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {/* The money-leg role is unmapped on this tenant — actionable, never
            silently replaced by a hardcoded account (spec I7 / §3.4 R1). */}
        {roleLegMissing && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between">
            <span>
              <AlertCircle size={16} className="me-2" />
              {neededRole === 'tax_account'
                ? 'Rollen Skattekonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'
                : 'Rollen Företagskonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              href={roleSettingsPath(companyId)}
            >
              Mappa rollen
            </Button>
          </Alert>
        )}

        {/* Step 1: choose a source. The choice itself is the navigation. */}
        {currentStep === 1 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Lightbulb size={18} className="me-2" style={{ color: '#f59e0b' }} />
                Hur vill du bokföra transaktionen?
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Transaktionen och beloppet är redan kända. Välj ett förslag, sök efter en mall eller ange kontona själv.
              </p>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <Button
                    className="w-100 h-100 text-start p-3"
                    variant="outline-secondary"
                    onClick={() => setDiscoveryMode('suggestions')}
                    style={discoveryMode === 'suggestions' ? { background: '#f1f5f9', borderColor: '#64748b', color: '#1e293b' } : {}}
                  >
                    <strong className="d-block">LedgerLoops förslag</strong>
                    <small>Utifrån transaktionen och tidigare mönster</small>
                  </Button>
                </div>
                <div className="col-md-4">
                  <Button
                    className="w-100 h-100 text-start p-3"
                    variant="outline-secondary"
                    onClick={() => setDiscoveryMode('templates')}
                    style={discoveryMode === 'templates' ? { background: '#f1f5f9', borderColor: '#64748b', color: '#1e293b' } : {}}
                  >
                    <strong className="d-block">Sök bokföringsmall</strong>
                    <small>Bläddra i hela mallbiblioteket</small>
                  </Button>
                </div>
                <div className="col-md-4">
                  <Button
                    className="w-100 h-100 text-start p-3"
                    variant="outline-secondary"
                    onClick={handleSkipSuggestions}
                  >
                    <strong className="d-block">Bokför manuellt</strong>
                    <small>Välj konton och kontering själv</small>
                  </Button>
                </div>
              </div>
            </div>

            {discoveryMode === 'suggestions' && loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Analyserar transaktion...
                </div>
              </div>
            )}

            {discoveryMode === 'suggestions' && !loading && suggestions.length > 0 && (
              <div>
                {suggestions.map((suggestion, index) => (
                  <Card
                    key={suggestion.proposal_id || index}
                    className="mb-2"
                    style={{
                      border: selectedSuggestion?.proposal_id === suggestion.proposal_id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                              {bookingSuggestionTitle(suggestion)}
                            </strong>
                            {Number.isFinite(suggestion.confidence) && (
                              <Badge bg={getConfidenceBadgeColor(suggestion.confidence)}>
                                {getConfidenceLabel(suggestion)}
                              </Badge>
                            )}
                            {suggestion.strategy && (
                              <Badge bg="secondary" style={{ fontSize: '0.625rem' }}>
                                {suggestion.strategy === 'historical' || suggestion.strategy === 'history' ? 'Historik' :
                                 suggestion.strategy === 'keyword' ? 'Nyckelord' :
                                 suggestion.strategy === 'rule' ? 'Regel' :
                                 suggestion.strategy === 'embedding' ? 'Liknande bokföring' :
                                 suggestion.strategy === 'template' ? 'Mall' : 'Standard'}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {suggestion.reason || 'Förslag baserat på tillgänglig information om transaktionen.'}
                          </div>
                          {Array.isArray(suggestion.evidence) && suggestion.evidence.some(evidenceLabel) && (
                            <div className="mt-2" style={{ fontSize: '0.75rem', color: '#475569' }}>
                              Underlag: {suggestion.evidence.map(evidenceLabel).filter(Boolean).slice(0, 2).join(' · ')}
                            </div>
                          )}
                          {Array.isArray(suggestion.rows) && suggestion.rows.length > 0 && (
                            <div className="mt-2 p-2" style={{ fontSize: '0.75rem', color: '#475569', background: '#f8fafc', borderRadius: 6 }}>
                              {suggestion.rows.map((row, rowIndex) => (
                                <div key={`${row.account_number}-${rowIndex}`} className="d-flex justify-content-between gap-3">
                                  <span>{row.account_number}{row.account_name ? ` – ${row.account_name}` : ''}</span>
                                  <span className="text-nowrap">
                                    {(Number(row.debit) || 0) > 0 ? `Debet ${formatCurrency(row.debit)}` : `Kredit ${formatCurrency(row.credit)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {Array.isArray(suggestion.warnings) && suggestion.warnings.length > 0 && (
                            <div className="mt-2" style={{ fontSize: '0.75rem', color: '#92400e' }}>
                              {suggestion.warnings.map(warningLabel).filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div>
                          {selectedSuggestion?.proposal_id === suggestion.proposal_id && (
                            <CheckCircle size={20} style={{ color: '#3b82f6' }} />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            {discoveryMode === 'suggestions' && !loading && suggestions.length === 0 && (
              <Alert variant="info">
                <AlertCircle size={16} className="me-2" />
                Inga automatiska förslag hittades. Du kan söka bland mallarna eller bokföra manuellt.
              </Alert>
            )}

            {discoveryMode === 'templates' && (
              <div>
                <Form.Control
                  type="search"
                  value={templateSearch}
                  onChange={(event) => setTemplateSearch(event.target.value)}
                  placeholder="Sök bland alla mallar..."
                  className="mb-3"
                />
                {templatesLoading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                {templateError && <Alert variant="danger">{templateError}</Alert>}
                {!templatesLoading && !selectedTemplate && visibleTemplates.map((template) => (
                  <Card
                    key={template.template_id}
                    className="mb-2"
                    role="button"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <Card.Body className="p-3">
                      <strong style={{ fontSize: '0.875rem' }}>{template.template_name}</strong>
                      {template.description && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{template.description}</div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
                {!templatesLoading && !selectedTemplate && visibleTemplates.length === 0 && (
                  <Alert variant="info">Inga mallar matchar sökningen.</Alert>
                )}
                {selectedTemplate && (
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <strong>{selectedTemplate.template_name}</strong>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{selectedTemplate.description}</div>
                        </div>
                        <Button size="sm" variant="link" onClick={() => setSelectedTemplate(null)}>Byt mall</Button>
                      </div>
                      {(selectedTemplate.body?.inputs || []).map((input) => (
                        <Form.Group className="mb-3" key={input.name}>
                          <Form.Label>{input.label || input.name}{input.required ? ' *' : ''}</Form.Label>
                          {input.type === 'account_picker' ? (
                            <Select
                              options={accounts}
                              value={accounts.find((account) => account.value === String(templateInputs[input.name])) || null}
                              onChange={(option) => setTemplateInputs((current) => ({ ...current, [input.name]: option?.value || '' }))}
                              placeholder="Välj konto..."
                              isClearable
                            />
                          ) : input.type === 'select' ? (
                            <Form.Select
                              value={templateInputs[input.name] ?? ''}
                              onChange={(event) => setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                            >
                              <option value="">Välj...</option>
                              {(input.options || []).map((option) => {
                                const value = typeof option === 'object' ? option.value : option;
                                const label = typeof option === 'object' ? option.label : option;
                                return <option key={value} value={value}>{label}</option>;
                              })}
                            </Form.Select>
                          ) : input.type === 'boolean' ? (
                            <Form.Check
                              type="checkbox"
                              checked={Boolean(templateInputs[input.name])}
                              onChange={(event) => setTemplateInputs((current) => ({ ...current, [input.name]: event.target.checked }))}
                            />
                          ) : (
                            <Form.Control
                              type={input.type === 'date' ? 'date' : ['currency', 'currency_gross', 'currency_net', 'number', 'integer'].includes(input.type) ? 'number' : 'text'}
                              step={input.type === 'integer' ? '1' : '0.01'}
                              value={templateInputs[input.name] ?? ''}
                              onChange={(event) => setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                            />
                          )}
                          {input.help_text && <Form.Text>{input.help_text}</Form.Text>}
                        </Form.Group>
                      ))}
                      <Button onClick={handleApplyTemplate} disabled={templateEvaluating}>
                        {templateEvaluating ? <Spinner animation="border" size="sm" /> : 'Använd mall'}
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: one combined review/edit/booking screen. */}
        {currentStep === 2 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Edit3 size={18} className="me-2" style={{ color: '#3b82f6' }} />
                {selectedSuggestion ? 'Granska bokföringen' : 'Bokför manuellt'}
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                {selectedSuggestion
                  ? 'Kontrollera förslaget. Du kan ändra konteringsraderna innan du bokför.'
                  : 'Ange konton och belopp. Verifikatet måste balansera innan det kan bokföras.'}
              </p>
            </div>

            {selectedSuggestion && (
              <Card className="mb-3" style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <strong style={{ fontSize: '0.875rem' }}>{bookingSuggestionTitle(selectedSuggestion)}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                        {selectedSuggestion.reason || 'Automatiskt bokföringsförslag'}
                      </div>
                    </div>
                    <Badge bg={getConfidenceBadgeColor(selectedSuggestion.confidence)}>
                      {getConfidenceLabel(selectedSuggestion)}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Form>
              {/* Fiscal Year */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Räkenskapsår
                </Form.Label>
                <Form.Select
                  value={selectedFiscalYear || ''}
                  onChange={(e) => setSelectedFiscalYear(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                >
                  {fiscalYears.map((fy) => (
                    <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>
                      {fy.name} ({fy.start_date} - {fy.end_date})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Beskrivning
                </Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                />
              </Form.Group>

              {/* Verification rows: proposals are compact until the user opts into editing. */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                    Kontorader
                  </Form.Label>
                  {selectedSuggestion && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setShowAdvancedRows((visible) => !visible)}
                    >
                      <Edit3 size={14} className="me-1" />
                      {showAdvancedRows ? 'Visa kompakt' : 'Ändra konteringsrader'}
                    </Button>
                  )}
                </div>

                {!showAdvancedRows ? renderRowsTable() : (
                  <>
                    {rows.map((row, index) => (
                      <Row key={index} className="mb-2 align-items-center">
                        <Col md={6}>
                          <Select
                            options={accounts}
                            isSearchable
                            isClearable
                            placeholder="Välj konto..."
                            noOptionsMessage={() => 'Inga konton hittades'}
                            value={rowSelectValue(row)}
                            onChange={(option) => handleAccountSelect(index, option)}
                            styles={{
                              control: base => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                              menu: base => ({ ...base, fontSize: '0.875rem', zIndex: 5 })
                            }}
                          />
                        </Col>
                        <Col md={3} xs={5}>
                          <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="Debet"
                            value={row.debit}
                            onChange={(e) => updateRow(index, 'debit', e.target.value)}
                            style={{ fontSize: '0.875rem' }}
                          />
                        </Col>
                        <Col md={2} xs={5}>
                          <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="Kredit"
                            value={row.credit}
                            onChange={(e) => updateRow(index, 'credit', e.target.value)}
                            style={{ fontSize: '0.875rem' }}
                          />
                        </Col>
                        <Col md={1} xs={2} className="text-end">
                          {rows.length > 2 && (
                            <Button
                              variant="link"
                              className="p-1 text-danger"
                              onClick={() => handleRemoveRow(index)}
                              aria-label="Ta bort rad"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </Col>
                      </Row>
                    ))}

                    <Button variant="link" className="p-0" onClick={handleAddRow}>
                      <Plus size={14} className="me-1" />
                      Lägg till rad
                    </Button>
                  </>
                )}

                {/* Balance Check */}
                <Card style={{ backgroundColor: isBalanced ? '#f0fdf4' : '#fef2f2', border: 'none' }} className="mt-2">
                  <Card.Body className="p-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {isBalanced ? (
                          <><CheckCircle size={16} className="me-2" style={{ color: '#16a34a' }} />Balanserad</>
                        ) : (
                          <><AlertCircle size={16} className="me-2" style={{ color: '#dc2626' }} />Obalanserad</>
                        )}
                      </span>
                      <div style={{ fontSize: '0.875rem' }}>
                        <span className="me-3">Debet: {formatCurrency(totalDebit)}</span>
                        <span>Kredit: {formatCurrency(totalCredit)}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

            </Form>
          </div>
        )}

      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex align-items-center">
        <Button
          variant="outline-secondary"
          onClick={currentStep === startStep ? onHide : handleBack}
          disabled={creating}
        >
          <ChevronLeft size={16} className="me-1" />
          {currentStep === startStep ? 'Avbryt' : 'Tillbaka'}
        </Button>

        {currentStep === 2 && (
          <Button
            variant="success"
            onClick={handleCreateVerification}
            disabled={creating || !isBalanced || !rowsAreComplete || !selectedFiscalYear || fiscalYears.length === 0}
            className="ms-auto"
          >
            {creating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Bokför...
              </>
            ) : (
              <>
                <CheckCircle size={16} className="me-2" />
                Bokför verifikat
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateVerificationFromTransactionModal;
